import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Message } from 'semantic-ui-react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import Toolbar from 'components/common/Toolbar';
import ToolbarFooter from 'components/common/Toolbar/ToolbarFooter';
import Transactions from 'components/Wallet/Transactions';
import { ORGANIZATION_WALLET } from 'constants/walletTransactionsContext';
import Empty from 'components/common/Empty';
import {
  isFinanceRwandaAdmin,
  isSuperAdmin,
  isSupportAdmin,
  isOrganizationAdmin,
} from 'helpers/checkRoles';
import getTransactions from 'redux/actions/wallets/getTransactions';
import { getOrgTransactions } from 'redux/actions/wallets/getOrgRequests';
import getUserRoles from 'helpers/getUserRoles';
import isParentOrganization from 'helpers/isParentOrganization';

const AllTransactions = () => {
  const isCurrentOrgParent = isParentOrganization();
  const { search: searchQueries } = useLocation();
  const queryData = queryString.parse(searchQueries) || {};

  const {
    loading: loadingTransactions,
    data: transactions,
    error: transactionError,
    meta,
  } = useSelector(({ wallets: { transactions } }) => transactions);

  const {
    loading: loadingRequests,
    items: requests,
    error: errorRequests,
  } = useSelector(
    ({ wallets: { topupOrgRequests } }) => topupOrgRequests,
  );

  const { push } = useHistory();

  const { currentOrgId, orgUsers, roles } = getUserRoles();

  const hasAccess = () =>
    roles &&
    (isSuperAdmin(roles) ||
      isFinanceRwandaAdmin(roles) ||
      isSupportAdmin(roles) ||
      isOrganizationAdmin({ orgUsers, currentOrgId }));

  useEffect(() => {
    if (!hasAccess()) {
      push('/');
    }
  });

  const dispatch = useDispatch();

  const fetchTransactions = (queries = {}) => {
    if (isCurrentOrgParent) {
      getTransactions({ ...queryData, ...queries })(dispatch);
    } else {
      getOrgTransactions({
        organization_id: currentOrgId,
        ...queryData,
        ...queries,
      })(dispatch);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [dispatch]);

  const onQuery = queries => {
    fetchTransactions(queries);
  };

  return (
    <>
      <Toolbar
        header={{
          title: 'Transactions',
          navigation: true,
          rightComponent: (
            <ToolbarFooter
              onSearch={onQuery}
              onPaginate={onQuery}
              paginationMeta={meta}
              exportCSVData={transactions}
              exportCSVFileName="Transactions"
              filterTransactions
              onFilterTransactions={onQuery}
              filterTransactionsOptions={[
                'pending',
                'success',
                'failed',
              ]}
            />
          ),
        }}
      />
      {(transactionError || errorRequests) && (
        <Message negative>
          {transactionError?.message || errorRequests?.message}
        </Message>
      )}
      {isCurrentOrgParent
        ? !loadingTransactions &&
          !transactionError &&
          transactions?.length === 0 && (
            <Empty message="No transactions found" />
          )
        : !loadingRequests &&
          !errorRequests &&
          requests?.length === 0 && (
            <Empty message="No Requests found" />
          )}
      {isCurrentOrgParent
        ? !transactionError && (
            <Transactions
              loading={loadingTransactions}
              items={transactions ?? []}
              context={ORGANIZATION_WALLET}
              viewAll
              onTransactionRefresh={() => {
                fetchTransactions();
              }}
            />
          )
        : !errorRequests && (
            <Transactions
              loading={loadingRequests}
              items={requests ?? []}
              context={ORGANIZATION_WALLET}
              viewAll
              onTransactionRefresh={() => {
                fetchTransactions();
              }}
            />
          )}
    </>
  );
};

export default AllTransactions;
